import Card from "@rio-cloud/rio-uikit/Card";
import ContentLoader from "@rio-cloud/rio-uikit/ContentLoader";

interface DailyFleetExpenseChartSkeletonProps {
  points?: number;
  xTicks?: number;
  yTicks?: number;
}

const TITLE_LINE_HEIGHT = 29;
const LEGEND_LINE_HEIGHT = 17;
const AXIS_TICK_HEIGHT = 12;

const CHART_HEIGHT = 400;
const CHART_MARGIN = 15;
const X_AXIS_HEIGHT = 30;
const Y_AXIS_WIDTH = 80;
const PLOT_HEIGHT = CHART_HEIGHT - CHART_MARGIN * 2 - X_AXIS_HEIGHT;

const DOT_SIZE = 8;
const LEGEND_MARKER_WIDTH = 28;
const LEGEND_MARKER_HEIGHT = 2;

const POINT_HEIGHTS = [38, 52, 44, 66, 58, 74, 62, 85, 70, 56, 48, 63, 55, 78, 42];

export default function DailyFleetExpenseChartSkeleton({
  points = 15,
  xTicks = 8,
  yTicks = 6,
}: Readonly<DailyFleetExpenseChartSkeletonProps>) {
  const toPixels = (percentage: number, available: number) => Math.round((percentage / 100) * available);

  return (
    <Card className="padding-y-25 padding-x-15 padding-x-25-sm overflow-x-auto">
      <div className="padding-bottom-20">
        <span className="text-size-20 text-bold">
          <ContentLoader className="display-block" height={TITLE_LINE_HEIGHT} width={280} />
        </span>
      </div>

      <div className="height-400 width-700 width-100pct-md display-flex flex-column">
        <div style={{ height: CHART_MARGIN, flexShrink: 0 }} />

        <div className="display-flex" style={{ height: PLOT_HEIGHT }}>
          <div
            className="display-flex flex-column justify-content-between"
            style={{
              width: Y_AXIS_WIDTH + CHART_MARGIN,
              alignItems: "flex-end",
              paddingRight: 5,
              flexShrink: 0,
            }}
          >
            {Array.from({ length: yTicks }, (_, index) => (
              <ContentLoader key={index} height={AXIS_TICK_HEIGHT} width={50} />
            ))}
          </div>

          <div className="display-flex" style={{ flex: 1, justifyContent: "space-around", paddingRight: CHART_MARGIN }}>
            {Array.from({ length: points }, (_, index) => (
              <div
                key={index}
                className="display-flex flex-column"
                style={{ height: PLOT_HEIGHT, width: DOT_SIZE, justifyContent: "flex-end", flexShrink: 0 }}
              >
                <ContentLoader height={DOT_SIZE} width={DOT_SIZE} rx={DOT_SIZE / 2} ry={DOT_SIZE / 2} />
                <div
                  style={{
                    height: toPixels(POINT_HEIGHTS[index % POINT_HEIGHTS.length], PLOT_HEIGHT - DOT_SIZE),
                    flexShrink: 0,
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        <div
          className="display-flex align-items-center"
          style={{
            height: X_AXIS_HEIGHT,
            paddingLeft: Y_AXIS_WIDTH + CHART_MARGIN,
            paddingRight: CHART_MARGIN,
            justifyContent: "space-between",
          }}
        >
          {Array.from({ length: xTicks }, (_, index) => (
            <ContentLoader key={index} height={AXIS_TICK_HEIGHT} width={36} />
          ))}
        </div>

        <div style={{ height: CHART_MARGIN, flexShrink: 0 }} />
      </div>

      <div className="display-flex flex-wrap justify-content-center align-items-center gap-10 gap-25-sm padding-top-15 text-size-12 text-color-gray">
        <span className="display-flex align-items-center gap-10">
          <ContentLoader height={LEGEND_MARKER_HEIGHT} width={LEGEND_MARKER_WIDTH} />
          <ContentLoader height={LEGEND_LINE_HEIGHT} width={90} />
        </span>
        <span className="display-flex align-items-center gap-10">
          <ContentLoader height={LEGEND_MARKER_HEIGHT} width={LEGEND_MARKER_WIDTH} />
          <ContentLoader height={LEGEND_LINE_HEIGHT} width={90} />
        </span>
      </div>
    </Card>
  );
}
